import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { getPlayerImageUrl } from "../../helper/helper";
import type { MatchVm, TeamVm } from "../tournamentPage/matchVm";
import { infoFor, initials, matchUrl, oversText, type EmbedNav } from "./info";
import logo from "../../assets/images/criccenter_logo.png";
import { useReportHeight } from "./useReportHeight";
import "./embed.scss";

type Props = {
  vm: MatchVm;
  /** Tournament mode: arrows to move between matches. Omit for the single-match widget. */
  nav?: EmbedNav;
  now: number;
  polling?: boolean;
};

/** One line of text that steps down through `options` (longest first) until it fits its box, instead of cutting off with "…". */
const FitLine = ({ options, className }: { options: string[]; className?: string }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const key = options.join("|");
  const [i, setI] = useState(0);
  const [width, setWidth] = useState(0);
  useEffect(() => setI(0), [key]);
  // start over when the box changes width (the iframe was resized, or the next match has a different layout)
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let w = el.clientWidth;
    const ro = new ResizeObserver(() => { if (el.clientWidth !== w) { w = el.clientWidth; setWidth(w); setI(0); } });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  useLayoutEffect(() => {
    const el = ref.current;
    if (el && el.scrollWidth > el.clientWidth + 1 && i < options.length - 1) setI(i + 1);
  }, [i, key, options.length, width]);
  const text = options[Math.min(i, options.length - 1)];
  return <span ref={ref} className={className} title={options[0]}>{text}</span>;
};

const Badge = ({ t }: { t: TeamVm }) => (
  <span className="em-badge" style={{ background: t.color }}>
    {t.image ? <img src={getPlayerImageUrl(t.image) ?? ""} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : initials(t.name)}
  </span>
);

const TeamRow = ({ t, vm }: { t: TeamVm; vm: MatchVm }) => {
  // Super over / multi-innings matches come as "24/0 & 10/0 & 13/0"; the overs figure only describes the last part, so it is dropped
  const parts = (t.score ?? "").split("&").map((x) => x.trim()).filter(Boolean);
  const dim = vm.bucket === "live" ? !t.batting : vm.bucket === "past" ? !!t.lost : false;
  return (
    <div className={`em-team${dim ? " dim" : ""}`} title={t.name}>
      <Badge t={t} />
      <span className="em-code">{t.code}</span>
      {vm.bucket === "live" && t.batting && <i className="em-dot" aria-label="Batting" />}
      <span className={`em-score${parts.length > 1 ? " multi" : ""}`}>
        {vm.bucket === "up" ? <b>–</b> : parts.length > 1 ? parts.map((x, i) => <span key={i}>{i > 0 && <em>&amp;</em>}<b>{x}</b></span>) : t.score ? <><b>{t.score}</b><small>{oversText(t.overs)}</small></> : <b className="yet">{vm.bucket === "live" ? "Yet to bat" : "–"}</b>}
      </span>
    </div>
  );
};

const EmbedMatchCard = ({ vm, nav, now, polling }: Props) => {
  const ref = useRef<HTMLDivElement>(null);

  useReportHeight(ref);

  const up = vm.bucket === "up";
  const live = vm.bucket === "live";
  const tournament = vm.raw?.tournament?.tournamentName ?? vm.raw?.tournamentName ?? "";
  const title = [tournament, vm.label || (vm.no ? `Match ${vm.no}` : "")].filter(Boolean).join(" · ") || "Match";
  const info = infoFor(vm, now);

  return (
    <div className="em-card" ref={ref}>
      <div className="em-main">
        <div className="em-head">
          <img className="em-logo" src={logo} alt="CricCenter" />
          <span className="em-ttl">{title}</span>
          <span className={`em-chip ${vm.bucket}`}>
            {live && <i className={polling ? "pulse" : ""} />}
            {live ? "LIVE" : up ? "UPCOMING" : "RESULT"}
          </span>
          {nav && (
            <span className="em-nav">
              <button type="button" className="em-arrow" onClick={nav.onPrev} disabled={nav.index <= 0} aria-label="Previous match"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M15 5l-7 7 7 7" /></svg></button>
              <button type="button" className="em-arrow" onClick={nav.onNext} disabled={nav.index >= nav.total - 1} aria-label="Next match"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M9 5l7 7-7 7" /></svg></button>
            </span>
          )}
        </div>

        <div className="em-row">
          <div className="em-teams">
            <TeamRow t={vm.a} vm={vm} />
            <TeamRow t={vm.b} vm={vm} />
          </div>
          <div className="em-info">
            <span className="em-main-line" title={info.main}>{info.main}</span>
            {info.sub.length > 0 && <FitLine className="em-sub" options={info.sub} />}
          </div>
        </div>
      </div>

      <a className="em-cta" href={matchUrl(vm.id)} target="_blank" rel="noopener noreferrer">
        <span className="em-cta-ic"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M7 17L17 7M8 7h9v9" /></svg></span>
        <span className="em-cta-tx">{up ? <>Match<br />Center</> : <>Full<br />Scorecard</>}</span>
      </a>
    </div>
  );
};

export default EmbedMatchCard;
